import { useEvents } from "@magalu/ravejs-event-layer";
import { TomIconClose, TomIconInfo } from "@magalu/tom-icons/react/ts";
import React, { useCallback, useEffect, useState, memo } from "react";

type AlertFeedbackType = "error" | "success";

export type AlertFeedback = {
  type: AlertFeedbackType;
  message?: string;
  showIcon?: boolean;
};

type AlertData = AlertFeedback & {
  open: boolean;
};

const alertOptions: Record<AlertFeedbackType, { bgClass: string }> = {
  error: {
    bgClass: "bg-danger-darker",
  },
  success: {
    bgClass: "bg-success-darker",
  },
};

const defaultState: AlertData = {
  open: false,
  type: "error",
};

const AlertFeedback = () => {
  const [alertData, setAlertData] = useState<AlertData>(defaultState);
  const { subscribe, close } = useEvents();

  const openAlert = useCallback(
    (payload: { value: Record<string, unknown> }) => {
      const value = { ...payload.value, open: true };
      setAlertData(value as AlertData);
    },
    []
  );

  const closeAlert = () => {
    setAlertData(defaultState);
  };

  useEffect(() => {
    subscribe("alertfeedback:show", openAlert);

    return () => {
      close("alertfeedback:show", openAlert);
    };
  }, [openAlert]);

  if (!alertData.open || !alertData.message) {
    return null;
  }

  const styles = alertOptions[alertData.type];

  return (
    <div className="w-full fixed bottom-[135px] md:bottom-[75px] px-xlg left-[0px] flex items-center justify-center">
      <div
        data-testid={`alert-feedback-${alertData.type}`}
        className={`alert-feedback text-on-success-darker flex justify-between rounded-md px-md py-sm w-full md:w-[328px] ${styles?.bgClass}`}
      >
        <div className="flex items-center">
          {alertData.showIcon && (
            <TomIconInfo
              role="img"
              aria-label="info"
              name="info"
              width={24}
              height={24}
            />
          )}
          <div className="alert-feedback-container ml-md">
            <p className="alert-feedback-label font-2xsm-regular">
              {alertData.message}
            </p>
          </div>
        </div>
        <TomIconClose
          role="img"
          aria-label="close"
          name="close"
          className="cursor-pointer"
          width={24}
          height={24}
          onClick={closeAlert}
        />
      </div>
    </div>
  );
};

export default memo(AlertFeedback);
